import { Injectable } from '@angular/core';

export interface ResolvedPattern {
  pattern: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class PatternValidationService {

  private readonly patternVariables: Record<string, string> = {
    CAPITAL_NUMBER:             '^[A-Z0-9]+$',
    ALPHANUMERIC_SPACE:         '^[A-Za-z0-9 ]+$',
    NUMERIC_ONLY:               '^[0-9]+$',
    ALPHANUMERIC:               '^[A-Za-z0-9]+$',
    CAPITAL_TWO_LETTERS:        '^[A-Z0-9]{2}$',
    ALPHANUMERIC_SPACE_SPECIAL: '^[A-Za-z0-9 !@#$%^&*()_+\\-=\\[\\]{};\'\"\\\\|,.<>\\/]+$',
    EMAIL:                      '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,35}$',
    LETTERS_ONLY:               '^[A-Za-z]+$',
  };

  private readonly messageVariables: Record<string, string> = {
    CAPITAL_NUMBER_MSG:             'Only letters (Capital) and numbers allowed',
    ALPHANUMERIC_SPACE_MSG:         'Only alphanumeric characters and spaces',
    NUMERIC_ONLY_MSG:               'Only numeric values allowed',
    ALPHANUMERIC_MSG:               'Only alphanumeric characters allowed',
    CAPITAL_TWO_LETTERS_MSG:        'Only two capital letters allowed',
    ALPHANUMERIC_SPACE_SPECIAL_MSG: 'Only alphanumeric characters, spaces and special characters !@#$%^&*()_+-=[]{};\':"\\|,.<>/ allowed',
    EMAIL_MSG:                      'Invalid email format',
    LETTERS_ONLY_MSG:               'Only letters allowed',
  };

  /** Fallback pattern key per Java type — only used when metaData.pattern is absent.
   *  Decimal types (bigdecimal/double/float) are intentionally excluded:
   *  the indicators API always provides metaData.pattern for those fields.
   */
  private readonly typeDefaultPattern: Record<string, string> = {
    long:    'NUMERIC_ONLY',
    integer: 'NUMERIC_ONLY',
  };

  /**
   * Dynamically builds a regex for NUMBER_DECIMAL keys.
   * Supports formats:
   *   NUMBER_DECIMAL          → ^\d{1,11}(\.\d{1,4})?$  (default 11 digits, 4 decimal)
   *   NUMBER_DECIMAL_X        → ^\d{1,X}(\.\d{1,4})?$   (X digits, 4 decimal)
   *   NUMBER_DECIMAL_X_Y      → ^\d{1,X}(\.\d{1,Y})?$   (X digits, Y decimal)
   */
  private buildNumberDecimalPattern(key: string): string | null {
    if (key === 'NUMBER_DECIMAL') return '^\\d{1,7}(\\.\\d{1,4})?$';
    const match = key.match(/^NUMBER_DECIMAL_(\d+)(?:_(\d+))?$/);
    if (!match) return null;
    const total = +match[1];
    const dec   = +(match[2] ?? '4');
    const int   = match[2] ? total - dec : total;
    return `^\\d{1,${int}}(\\.\\d{1,${dec}})?$`;
  }

  private buildNumberDecimalMessage(key: string): string | null {
    if (key === 'NUMBER_DECIMAL_MSG') return 'Maximum 7 digits before the decimal point and 4 digits after it.';
    const match = key.match(/^NUMBER_DECIMAL_(\d+)(?:_(\d+))?_MSG$/);
    if (!match) return null;
    const total = +match[1];
    const dec   = +(match[2] ?? '4');
    const int   = match[2] ? total - dec : total;
    return `Maximum ${int} digits before the decimal point and ${dec} digits after it.`;
  }

  resolvePattern(patternKey: string): string {
    if (this.patternVariables[patternKey]) return this.patternVariables[patternKey];
    const dynamic = this.buildNumberDecimalPattern(patternKey);
    return dynamic ?? patternKey;
  }

  resolveMessage(messageKey: string): string {
    if (this.messageVariables[messageKey]) return this.messageVariables[messageKey];
    const dynamic = this.buildNumberDecimalMessage(messageKey);
    return dynamic ?? messageKey;
  }

  /**
   * Returns true if the attribute has a pattern (from metaData or type default).
   * Used to decide whether to render a text input instead of nz-input-number
   * so that HTML pattern validation works.
   */
  hasPattern(attr: any): boolean {
    return this.resolveAttributePattern(attr) !== null;
  }

  /**
   * Resolve pattern + error message for an attribute object.
   * Priority: attr.metaData.pattern > Java type default > null
   */
  resolveAttributePattern(attr: any): ResolvedPattern | null {
    if (!attr) return null;
    let meta = attr?.metaData ?? attr?.metadata;
    // API may return metaData as a JSON string — parse it
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta); } catch { meta = null; }
    }
    const patternKey: string = meta?.pattern || this.typeDefaultPattern[attr?.type?.toLowerCase()?.replace(/\s+/g, '') ?? ''] || '';
    if (!patternKey) return null;
    const msgKey: string = meta?.errorMessages?.pattern || (patternKey + '_MSG');
    return {
      pattern: this.resolvePattern(patternKey),
      message: this.resolveMessage(msgKey),
    };
  }
}
